import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useUpdateSqPrefillPreferenceMutation } from './__generated__/useSqPrefillPreference.generated';
import type { SqPastAnswerPrefillPreference } from '../../../__generated__/graphql-base-types';
import { modifyAutoApplyInfo } from '../../../lib/apollo/client';

interface UseSqPrefillPreferenceResult {
    usePastAnswers: boolean;
    /** Returns true on success, false on failure. Callers that don't need the result can use `void`. */
    handleUsePastAnswersChange: (checked: boolean) => Promise<boolean>;
    /** Error from the most recent mutation attempt, cleared on next attempt */
    error: Error | undefined;
    /** Whether the current error is a server response that contradicted the requested value. */
    isVerificationError: boolean;
    /** True while the preference mutation is in progress. */
    saving: boolean;
}

export const useSqPrefillPreference = (
    serverValue: string | null | undefined,
): UseSqPrefillPreferenceResult => {
    const apolloClient = useApolloClient();
    const [verificationError, setVerificationError] = useState<Error>();
    const [updateSqPrefillPreference, { error: mutationError, loading: saving }] =
        useUpdateSqPrefillPreferenceMutation();
    const error = verificationError ?? mutationError;

    // Derive toggle state directly from server value (defaults to true before data loads)
    const usePastAnswers = serverValue !== 'OPT_OUT';

    const handleUsePastAnswersChange = useCallback(
        (checked: boolean): Promise<boolean> => {
            const newPreference: SqPastAnswerPrefillPreference = checked ? 'OPT_IN' : 'OPT_OUT';
            setVerificationError(undefined);

            return updateSqPrefillPreference({
                variables: {
                    input: { sqPastAnswerPrefillPreference: newPreference },
                },
            })
                .then(({ data: mutationData }) => {
                    const savedPreference =
                        mutationData?.updateAutoApply.sqPastAnswerPrefillPreference;
                    if (savedPreference !== newPreference) {
                        setVerificationError(
                            new Error('SQ prefill preference response did not match the request'),
                        );
                        return false;
                    }

                    modifyAutoApplyInfo(apolloClient.cache, {
                        sqPastAnswerPrefillPreference() {
                            return savedPreference;
                        },
                    });
                    return true;
                })
                .catch((error: unknown) => {
                    console.error('Failed to update SQ prefill preference:', error);
                    return false;
                });
        },
        [apolloClient.cache, updateSqPrefillPreference],
    );

    return {
        usePastAnswers,
        handleUsePastAnswersChange,
        error,
        isVerificationError: verificationError !== undefined,
        saving,
    };
};
